import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  Edit,
  Phone,
  Play,
  StopCircle,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppointment } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  addMinutesToTime,
  fmtDateLong,
  fmtMoney,
  PAYMENT_LABEL,
  STATUS_COLOR,
  STATUS_DOT,
  STATUS_LABEL,
} from '@/lib/format';
import { tgConfirm, hapticNotify, haptic } from '@/lib/telegram';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui/avatar';
import { StockWriteoffDialog } from '@/components/StockWriteoffDialog';
import { useBackButton } from '@/lib/tg-buttons';
import { useAuthStore } from '@/store/auth';

export const AppointmentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: a, isLoading, isError, error, refetch } = useAppointment(id);
  const [now, setNow] = useState(new Date());
  const isViewer = useAuthStore((s) => s.master?.role) === 'viewer';
  useBackButton(() => nav(-1));

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(i);
  }, []);

  const total = useMemo(() => {
    if (!a) return 0;
    const ex = (a.extras ?? []).reduce((s: number, e: any) => s + Number(e.price), 0);
    return Number(a.work_price) + Number(a.hair_price) + Number(a.tangle_price ?? 0) + ex;
  }, [a]);

  if (isError) {
    return (
      <div className="space-y-3 p-2">
        <Card>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="font-medium text-destructive">
              {(error as any)?.response?.data?.error ?? 'Не удалось загрузить запись'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => refetch()} className="flex-1">
                Повторить
              </Button>
              <Button variant="ghost" onClick={() => nav(-1)} className="flex-1">
                Назад
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (isLoading || !a) {
    return (
      <div className="space-y-3 p-2">
        <div className="skeleton h-10 w-40" />
        <div className="skeleton h-32 w-full" />
        <div className="skeleton h-24 w-full" />
        <div className="skeleton h-40 w-full" />
      </div>
    );
  }

  const callApi = async (path: string, body?: unknown) => {
    try {
      await api.post(`/appointments/${id}${path}`, body);
      hapticNotify('success');
      refetch();
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['appointment', id] });
    } catch (e: any) {
      hapticNotify('error');
      alert(e?.response?.data?.error ?? 'Ошибка');
    }
  };

  const onDelete = async () => {
    if (!(await tgConfirm('Удалить запись?'))) return;
    try {
      await api.delete(`/appointments/${id}`);
      qc.invalidateQueries({ queryKey: ['appointments'] });
      nav(-1);
    } catch (e: any) {
      hapticNotify('error');
      alert(e?.response?.data?.error ?? 'Не удалось удалить запись');
    }
  };

  const onCancel = async () => {
    if (!(await tgConfirm('Отменить запись?'))) return;
    await callApi('/cancel', { reason: 'Отменено мастером' });
  };

  const isActive = a.status === 'in_progress';

  return (
    <div className="animate-in-up space-y-4 pb-2">
      <div className="flex items-center justify-end">
        <Badge className={cn(STATUS_COLOR[a.status])}>
          <span className={cn('status-dot', STATUS_DOT[a.status])} />
          {STATUS_LABEL[a.status]}
        </Badge>
      </div>

      {/* Hero */}
      <Card className="bg-aurora-card overflow-hidden">
        <CardContent className="space-y-3 p-4">
          {isViewer ? (
            <div className="flex items-center gap-3">
              <Avatar name={a.client.name} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xl font-bold">{a.client.name}</div>
              </div>
            </div>
          ) : (
            <Link to={`/clients/${a.client.id}`} className="flex items-center gap-3">
              <Avatar name={a.client.name} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xl font-bold">{a.client.name}</div>
                {a.client.phone && (
                  <a
                    href={`tel:${a.client.phone}`}
                    className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="h-3 w-3" /> {a.client.phone}
                  </a>
                )}
              </div>
            </Link>
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{
                background: `${a.service.color}1f`,
                color: a.service.color,
              }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: a.service.color }}
              />
              {a.service.name}
            </span>
            <span className="text-muted-foreground capitalize">{fmtDateLong(a.date)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="tabular">
              {a.start_time}–{addMinutesToTime(a.start_time, a.planned_duration_min)}
            </span>
          </div>
        </CardContent>
      </Card>

      {(a.strands_count || a.hair_grams || a.hair_added) && (
        <Card>
          <CardContent className="grid grid-cols-3 gap-2 p-4 text-center">
            {a.strands_count && <StatCell label="Прядей" value={String(a.strands_count)} />}
            {a.hair_length_cm && <StatCell label="Длина" value={`${a.hair_length_cm} см`} />}
            {a.hair_grams && <StatCell label="Грамм" value={`${Number(a.hair_grams)} г`} />}
          </CardContent>
        </Card>
      )}

      {!isViewer && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Row label="Работа" value={fmtMoney(Number(a.work_price))} />
            {a.hair_added && <Row label="Волосы" value={fmtMoney(Number(a.hair_price))} />}
            {a.tangle_removal && (
              <Row label="Распутывание колтунов" value={fmtMoney(Number(a.tangle_price))} />
            )}
            {(a.extras ?? []).map((e: any) => (
              <Row key={e.id} label={`+ ${e.extra_service.name}`} value={fmtMoney(Number(e.price))} />
            ))}
            <div className="my-2 border-t border-border/60" />
            <Row label="Итого" value={fmtMoney(total)} bold />
            {a.payment_method && (
              <Row
                label="Оплата"
                value={`${PAYMENT_LABEL[a.payment_method]} · ${fmtMoney(Number(a.paid_amount))}`}
              />
            )}
            {Number(a.debt_amount) > 0 && (
              <Row
                label="Долг"
                value={fmtMoney(Number(a.debt_amount))}
                className="text-warning"
              />
            )}
          </CardContent>
        </Card>
      )}

      {!isViewer && a.note && (
        <Card>
          <CardContent className="p-4 text-sm">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Заметка
            </div>
            {a.note}
          </CardContent>
        </Card>
      )}

      {!isViewer && isActive && <ActiveAppointmentActions appointment={a} onChange={refetch} />}

      {!isViewer && (
        <div className="grid grid-cols-2 gap-2">
          {a.status === 'scheduled' && (
            <Button variant="outline" onClick={() => callApi('/confirm')}>
              <CheckCircle2 className="h-4 w-4" /> Подтвердить
            </Button>
          )}
          {(a.status === 'scheduled' || a.status === 'confirmed') && (
            <Button onClick={() => callApi('/start')}>
              <Play className="h-4 w-4" /> Начать
            </Button>
          )}
          {(a.status === 'scheduled' || a.status === 'confirmed') && (
            <Button variant="outline" onClick={() => nav(`/appointments/${id}/edit`)}>
              <Edit className="h-4 w-4" /> Изменить
            </Button>
          )}
          {(a.status === 'scheduled' || a.status === 'confirmed') && (
            <Button variant="outline" onClick={() => nav(`/appointments/${id}/reschedule`)}>
              <Clock className="h-4 w-4" /> Перенести
            </Button>
          )}
          {(a.status === 'scheduled' || a.status === 'confirmed') && (
            <Button variant="outline" onClick={() => callApi('/no-show')}>
              <XCircle className="h-4 w-4" /> Не пришёл
            </Button>
          )}
          {a.status !== 'completed' && a.status !== 'cancelled' && (
            <Button variant="destructive" onClick={onCancel}>
              <XCircle className="h-4 w-4" /> Отменить
            </Button>
          )}
        </div>
      )}

      {!isViewer && (
        <Button variant="ghost" onClick={onDelete} className="w-full text-destructive">
          <Trash2 className="h-4 w-4" /> Удалить запись
        </Button>
      )}
    </div>
  );
};

const Row = ({
  label,
  value,
  bold,
  className,
}: {
  label: string;
  value: string;
  bold?: boolean;
  className?: string;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={cn(
        bold ? 'text-lg font-bold tabular' : 'font-semibold tabular',
        className,
      )}
    >
      {value}
    </span>
  </div>
);

const StatCell = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl bg-secondary p-3">
    <div className="text-base font-bold tabular">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

const ActiveAppointmentActions = ({ appointment: a, onChange }: { appointment: any; onChange: () => void }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const start = a.actual_start_at ? new Date(a.actual_start_at).getTime() : Date.now();
  const elapsed = Math.floor((Date.now() - start) / 1000);
  const planned = a.planned_duration_min * 60;
  const remaining = planned - elapsed;

  const fmtSec = (s: number) => {
    const sign = s < 0 ? '+' : '';
    const abs = Math.abs(s);
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const sec = abs % 60;
    if (h) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    return `${sign}${m}:${String(sec).padStart(2, '0')}`;
  };

  const extend = async (minutes: number) => {
    haptic('medium');
    await api.post(`/appointments/${a.id}/extend`, { extra_minutes: minutes });
    onChange();
  };

  const overtime = remaining < 0;

  return (
    <Card className="overflow-hidden ring-2 ring-primary/40">
      <div className="bg-brand p-4 text-white">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-90">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
          </span>
          {overtime ? 'Превышено время' : 'Идёт процедура'}
        </div>
        <div className="mt-2 text-center">
          <div className="text-5xl font-bold tabular leading-none">{fmtSec(remaining)}</div>
          <div className="mt-1 text-xs uppercase tracking-wider opacity-80">
            {overtime ? 'переработка' : 'осталось'}
          </div>
        </div>
      </div>
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" size="sm" onClick={() => extend(15)}>
            +15м
          </Button>
          <Button variant="outline" size="sm" onClick={() => extend(30)}>
            +30м
          </Button>
          <Button variant="outline" size="sm" onClick={() => extend(60)}>
            +60м
          </Button>
        </div>
        {a.hair_added && Number(a.hair_grams) > 0 && (
          <StockWriteoffDialog
            appointmentId={a.id}
            defaultGrams={Number(a.hair_grams)}
            preferredLengthCm={a.hair_length_cm ?? undefined}
            alreadyWrittenOff={(a.stock_movements ?? [])
              .filter((m: any) => m.type === 'out')
              .reduce((s: number, m: any) => s + Number(m.grams), 0)}
            onDone={onChange}
          />
        )}
        <FinishDialog appointment={a} onChange={onChange} />
      </CardContent>
    </Card>
  );
};

const FinishDialog = ({ appointment: a, onChange }: { appointment: any; onChange: () => void }) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<string>('cash');
  const total = Number(a.work_price) + Number(a.hair_price) + Number(a.tangle_price ?? 0) + (a.extras ?? []).reduce((s: number, e: any) => s + Number(e.price), 0);
  const [paid, setPaid] = useState<number>(total);
  const [paidTouched, setPaidTouched] = useState(false);
  const [debt, setDebt] = useState<number>(0);
  const [showWriteoff, setShowWriteoff] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Keep `paid` synced with the live `total` until the user manually edits it.
  useEffect(() => {
    if (!paidTouched) setPaid(total);
  }, [total, paidTouched]);

  // When the dialog opens, reset "touched" state so re-opening reflects fresh totals.
  useEffect(() => {
    if (open) setPaidTouched(false);
  }, [open]);

  const writtenOff = (a.stock_movements ?? [])
    .filter((m: any) => m.type === 'out')
    .reduce((s: number, m: any) => s + Number(m.grams), 0);
  const needWriteoff =
    a.hair_added &&
    Number(a.hair_grams) > 0 &&
    writtenOff < Number(a.hair_grams) - 0.01;

  const finish = async () => {
    if (submitting) return;
    if (needWriteoff && !showWriteoff) {
      const ok = await tgConfirm(
        `Списать ${(Number(a.hair_grams) - writtenOff).toFixed(0)} г волос со склада?\n\nЕсли уже списано — нажмите «Отмена», запись завершится без списания.`,
      );
      if (ok) {
        setShowWriteoff(true);
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/appointments/${a.id}/finish`, {
        payment_method: method,
        paid_amount: paid,
        debt_amount: method === 'debt' ? Math.max(0, total - paid) : debt,
        actual_end_at: new Date().toISOString(),
      });
      setOpen(false);
      hapticNotify('success');
      onChange();
    } catch (e: any) {
      hapticNotify('error');
      alert(e?.response?.data?.error ?? 'Не удалось завершить процедуру');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full" size="lg">
          <StopCircle className="h-5 w-5" /> Завершить процедуру
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Завершение процедуры</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-2xl bg-brand p-4 text-center text-white shadow-soft">
            <div className="text-xs font-bold uppercase tracking-widest opacity-90">
              К оплате
            </div>
            <div className="mt-1 text-4xl font-bold tabular leading-tight">
              {fmtMoney(total)}
            </div>
          </div>
          <div>
            <Label>Способ оплаты</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Наличные</SelectItem>
                <SelectItem value="card">Карта</SelectItem>
                <SelectItem value="transfer">Перевод</SelectItem>
                <SelectItem value="debt">В долг</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Оплачено (₽)</Label>
            <Input
              type="number"
              value={paid}
              onChange={(e) => {
                setPaidTouched(true);
                setPaid(Number(e.target.value));
              }}
            />
          </div>
          {(method === 'debt' || paid < total) && (
            <div className="rounded-xl bg-warning-soft p-3 text-sm font-medium text-warning">
              Долг: {fmtMoney(Math.max(0, total - paid))}
            </div>
          )}
          {needWriteoff && (
            <div className="rounded-xl bg-primary-soft p-3 text-xs text-primary">
              По записи нужно списать <b>{(Number(a.hair_grams) - writtenOff).toFixed(0)} г</b> волос.
              Спросим перед завершением.
            </div>
          )}
          <Button onClick={finish} disabled={submitting} size="lg" className="w-full">
            <CheckCircle2 className="h-4 w-4" /> Завершить
          </Button>
        </div>
        {showWriteoff && (
          <StockWriteoffDialog
            appointmentId={a.id}
            defaultGrams={Number(a.hair_grams)}
            preferredLengthCm={a.hair_length_cm ?? undefined}
            alreadyWrittenOff={writtenOff}
            autoOpen
            onDone={() => {
              setShowWriteoff(false);
              onChange();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
